import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AssistantOutlined, SendOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { makeApiCall } from '../../config/api';
import MarkdownContent from './MarkdownContent';

const CopilotPanel = () => {
  const { t, i18n } = useTranslation();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const ask = async () => {
    const trimmed = question.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const response = await makeApiCall('api/insights/copilot/ask', {
        method: 'POST',
        body: JSON.stringify({
          question: trimmed,
          locale: (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en',
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || t('insights.copilot.failed'));
      setAnswer(payload);
    } catch (askError) {
      setError(askError.message || t('insights.copilot.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined" component="section" aria-labelledby="insights-copilot-title">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <AssistantOutlined sx={{ color: '#1565c0' }} aria-hidden="true" />
          <Typography id="insights-copilot-title" variant="h6" component="h3">
            {t('insights.copilot.title')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('insights.copilot.description')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            label={t('insights.copilot.questionLabel')}
            inputProps={{ 'aria-label': t('insights.copilot.questionAria') }}
          />
          <Button
            variant="contained"
            onClick={ask}
            disabled={loading || !question.trim()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendOutlined />}
            aria-label={t('insights.copilot.askAria')}
            sx={{ minWidth: { sm: 130 } }}
          >
            {t('insights.copilot.ask')}
          </Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {answer?.answer && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#f4f8fb', borderLeft: '4px solid #1565c0', borderRadius: 1 }}>
            <MarkdownContent>{answer.answer}</MarkdownContent>
            {answer.citations?.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('insights.copilot.sources', { sources: answer.citations.join(', ') })}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CopilotPanel;
